import React, { useState } from 'react';
import { Box, ButtonBase, Tooltip } from '@material-ui/core';
import { CustomIcon } from '../../../custom-forms';
import { useStyles, cx } from '../../../custom-forms';
import { GRID_COLUMNS, GridColumnConfig } from '../config/columns';
import {
  ColumnFilter,
  ColumnKey,
  FiltersState,
  GridColumn,
  SortState
} from '../types/alternative-calculation.types';
import { useTranslate } from '../../../hooks/useTranslate';
import { useVariantsEnabled } from '../context/CalculationFlagsContext';
import FilterPopover from '../../grid-filters/FilterPopover';

export interface AlternativeCalculationPiecesHeaderProps {
  sort: SortState;
  filters: FiltersState;
  onSort: (key: ColumnKey) => void;
  onApplyFilter: (key: ColumnKey, value: ColumnFilter | null) => void;
  filterOptionsFor: (key: ColumnKey) => ColumnFilter[];
  filterOptionLabel: (key: ColumnKey, option: ColumnFilter | null) => string;
  onUnselectAll: (() => void) | null;
  hasUnselectAllColumn: boolean;
  hasRecoColumn: boolean;
  columns: GridColumnConfig[];
  // Flush styling for the variants header nested inside an order-part card (OrderDetailsNew).
  flush: boolean;
}

const AlternativeCalculationPiecesHeader: React.FC<AlternativeCalculationPiecesHeaderProps> = ({
  sort,
  filters,
  onSort,
  onApplyFilter,
  filterOptionsFor,
  filterOptionLabel,
  onUnselectAll,
  hasUnselectAllColumn,
  hasRecoColumn,
  columns,
  flush
}) => {
  const c = useStyles();
  const t = useTranslate();
  const variantsEnabled = useVariantsEnabled();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activeColumn, setActiveColumn] = useState<GridColumn | null>(null);

  const openFilter = (column: GridColumn, anchor: HTMLElement) => {
    setActiveColumn(column);
    setAnchorEl(anchor);
  };
  const closeFilter = () => {
    setAnchorEl(null);
    setActiveColumn(null);
  };

  const renderHeaderColumn = (col: GridColumnConfig) => {
    const sortKey = col.sortKey;
    if (!sortKey) {
      return (
        <Box key={col.key} className={cx(c.headCell, col.numeric && c.justifyEnd)}>
          {col.renderHeaderCell()}
        </Box>
      );
    }
    const active = sort.column === sortKey;
    return (
      <Box key={col.key} className={cx(c.headCell, col.numeric && c.justifyEnd)}>
        <Box
          component="span"
          className={cx(c.sortLabel, active && c.sortActive)}
          onClick={() => onSort(sortKey)}
        >
          {col.renderHeaderCell()}
          {active && (
            <Box component="span" className={c.sortArrow}>
              <CustomIcon name={sort.dir === -1 ? 'sortDesc' : 'sortAsc'} />
            </Box>
          )}
        </Box>
        {col.filterable && (
          <Tooltip title={t('ALT_CALC_FILTER_BY', 'Filtrează')} placement="bottom">
            <ButtonBase
              className={cx(c.filterBtn, filters[sortKey] && c.filterBtnOn)}
              onClick={(e) => {
                const gridColumn = GRID_COLUMNS.find((g) => g.key === sortKey);
                if (gridColumn) openFilter(gridColumn, e.currentTarget);
              }}
            >
              <CustomIcon name="funnel" />
            </ButtonBase>
          </Tooltip>
        )}
      </Box>
    );
  };

  return (
    <Box className={cx(c.gridRow, c.gridHead, flush && c.gridHeadFlush, c.gutterReserve)}>
      {hasUnselectAllColumn && (
        <Box>
          {onUnselectAll && (
            <Tooltip
              title={t('ALT_CALC_UNSELECT_ALL', 'Deselectează tot (toate piesele)')}
              placement="bottom-start"
            >
              <ButtonBase className={cx(c.iconBtn, c.iconBtnSm)} onClick={onUnselectAll}>
                <CustomIcon name="ban" />
              </ButtonBase>
            </Tooltip>
          )}
        </Box>
      )}
      {hasRecoColumn && <Box />}

      {columns.map(renderHeaderColumn)}

      <FilterPopover
        anchorEl={anchorEl}
        column={activeColumn}
        value={activeColumn ? filters[activeColumn.key] : undefined}
        options={activeColumn ? filterOptionsFor(activeColumn.key) : []}
        getOptionLabel={(o) => (activeColumn ? filterOptionLabel(activeColumn.key, o) : '')}
        onApply={(value) => {
          if (activeColumn) onApplyFilter(activeColumn.key, value);
          closeFilter();
        }}
        onClose={closeFilter}
      />
    </Box>
  );
};

export default AlternativeCalculationPiecesHeader;
